import { concat, findIndex, remove, whereEq } from "ramda";
import * as React from "react";
import { connect } from "react-redux";
import { getUserId } from "src/login/selector";
import TextInput from "src/shared/TextInput";
import { isRequesting } from "../requestProgress/helpers";
import { actions } from "../requestProgress/reducer";
import { RequestProgressState } from "../requestProgress/reducer";
import { getRequestProgress } from "../requestProgress/selector";
import TextInputWithButton from "../shared/TextInputWithButton";
import { State } from "../state";
import { addComment, AddCommentActionType } from "./actions";
import { Comment } from "./types";

interface CommentInputProps {
  requestProgress: RequestProgressState;
  className?: string;
  addComment: AddCommentActionType;
  userId: string;
  spreadId?: string;
  accountId?: string;
  accountName?: string;
  updateComments: (comments: Comment[]) => void;
  dontPostToBackend?: boolean;
  exitEditingMode?: () => void;
  commentBeingEdited?: Comment | null;
  commentBeingDeleted?: Comment | null;
  onClose?: (event?: React.SyntheticEvent<{}>) => void;
  isHidden?: boolean;
  comments: Comment[] | null;
  buttonText?: string;
  placeholder?: string;
  period?: string;
  withoutButton?: boolean;
  submitForm?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  hasFocus?: boolean;
}

interface CommentInputState {
  commentText: string;
  hasPrefilledCommentTextForEditing: boolean;
  commentBeingDeleted: Comment | null;
  formSubmited?: boolean;
}

export class CommentInput extends React.Component<
  CommentInputProps,
  CommentInputState
> {
  public static getDerivedStateFromProps(
    nextProps: CommentInputProps,
    prevState: CommentInputState
  ) {
    const { commentBeingEdited } = nextProps;
    const { hasPrefilledCommentTextForEditing } = prevState;

    if (commentBeingEdited && !hasPrefilledCommentTextForEditing) {
      return {
        commentText: commentBeingEdited.body,
        hasPrefilledCommentTextForEditing: true
      };
    }
    return null;
  }

  constructor(props: CommentInputProps) {
    super(props);
    this.state = {
      commentText: "",
      hasPrefilledCommentTextForEditing: false,
      commentBeingDeleted: null,
      formSubmited: false
    };
  }

  public componentDidUpdate(prevProps: CommentInputProps) {
    if (!prevProps.commentBeingDeleted && this.props.commentBeingDeleted) {
      this.deleteCommentLocally(this.props.commentBeingDeleted);
    }

    if (this.props.submitForm && !this.state.formSubmited) {
      this.setState({ formSubmited: true });
      this.onFormSubmit();
    }
  }

  public render() {
    const { className, commentBeingEdited } = this.props;
    const buttonText = commentBeingEdited ? "UPDATE" : "ADD";

    if (this.props.isHidden) {
      return null;
    }

    return (
      <form
        className={className ? `CommentInput ${className}` : "CommentInput"}
        onSubmit={this.onFormSubmit}
      >
        {!this.props.withoutButton && (
          <TextInputWithButton
            autoFocus={this.props.hasFocus}
            onChange={this.onTextChange}
            placeholder={this.props.placeholder || "Add a comment..."}
            buttonProps={{
              quiqButtonProps: {
                type: "submit",
                color: "blue",
                onClick: this.onSubmit,
                isDisabled: this.state.commentText === ""
              },

              buttonText: this.props.buttonText || buttonText
            }}
            value={this.state.commentText}
            isLoading={isRequesting(actions.addComment)}
          />
        )}

        {this.props.withoutButton && (
          <TextInput
            placeholder={this.props.placeholder || "Add a comment..."}
            value={this.state.commentText}
            onChange={this.onTextChange}
          />
        )}
      </form>
    );
  }

  private handleAddComment = () => {
    if (!this.props.dontPostToBackend && this.props.spreadId) {
      this.props
        .addComment({
          commentText: this.state.commentText,
          username: this.props.userId,
          spreadId: this.props.spreadId,
          accountId: this.props.accountId,
          accountName: this.props.accountName,
          period: this.props.period
        })
        .then(({ comment }) => {
          this.updateCommentsLocally(comment);
        });
    } else {
      const comment: Comment = {
        body: this.state.commentText,
        username: this.props.userId,
        readBy: [this.props.userId],
        period: this.props.period
      };
      this.updateCommentsLocally(comment);
    }
    if (this.props.commentBeingEdited && this.props.exitEditingMode) {
      this.props.exitEditingMode();
    }
  };

  private deleteCommentLocally = (comment: Comment) => {
    if (!this.props.comments) {
      return;
    }
    const commentIndex = findIndex(whereEq(comment), this.props.comments);
    const newComments = remove(commentIndex, 1, this.props.comments);
    this.props.updateComments(newComments);
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  private updateCommentsLocally = (comment: Comment) => {
    if (this.props.commentBeingEdited) {
      this.props.updateComments([comment]);
    } else {
      const comments: Comment[] = concat([comment])(this.props.comments || []);
      this.props.updateComments(comments);
    }
    this.resetCommentText();
  };

  private resetCommentText = () => {
    this.setState({
      commentText: "",
      hasPrefilledCommentTextForEditing: false
    });
  };

  private onFormSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
    }

    if (
      this.state.commentText.length < 1 ||
      !/\S/.test(this.state.commentText)
    ) {
      return;
    }

    this.handleAddComment();
  };

  private onSubmit = () => {
    if (
      this.state.commentText.length < 1 ||
      !/\S/.test(this.state.commentText)
    ) {
      return;
    }
    this.handleAddComment();
  };

  private onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (this.props.onChange) {
      this.props.onChange(e);
    }
    this.setState({ commentText: e.target.value });
  };
}

const mapStateToProps = (state: State) => ({
  userId: getUserId(state),
  requestProgress: getRequestProgress(state)
});

export default connect(
  mapStateToProps,
  {
    addComment: addComment.action
  }
)(CommentInput);



// WEBPACK FOOTER //
// ./src/commentView/CommentInput.tsx